import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Megaphone, Plus, X, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useDealPrograms } from '@/data/hooks/useDealPrograms';
import { CATEGORY_LABELS } from '@/data/hooks/usePrograms';
import { countdownLabel, urgencyTier } from '@/lib/programStatus';
import { AttachProgramDialog } from './AttachProgramDialog';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function LinkedProgramsCard({
  dealId, dealershipId,
}: { dealId: string; dealershipId: string }) {
  const { mode, userId, role } = useAuth();
  const { links, refresh } = useDealPrograms(dealId);
  const [open, setOpen] = useState(false);
  const attachedIds = useMemo(() => new Set(links.map(l => l.program_id)), [links]);

  const unlink = async (linkId: string, attachedBy: string) => {
    if (mode === 'demo') { toast.info('Demo mode'); return; }
    const canUnlink = attachedBy === userId || role === 'manager' || role === 'admin';
    if (!canUnlink) { toast.error('Only the person who attached or a manager can unlink'); return; }
    const { error } = await supabase.from('deal_programs').delete().eq('id', linkId);
    if (error) { toast.error('Could not unlink'); return; }
    refresh();
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Megaphone className="h-4 w-4 text-primary" /> Linked Programs
          </CardTitle>
          <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setOpen(true)}>
            <Plus className="h-3.5 w-3.5" /> Attach
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {links.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No programs attached. Link an incentive, lease offer, or rebate so it shows on the deal timeline.
            </p>
          )}
          {links.map(l => {
            const tier = urgencyTier(l.program);
            const expired = tier === 'expired';
            return (
              <div
                key={l.id}
                className={`flex items-start justify-between gap-3 rounded-lg border p-3 ${
                  expired ? 'border-red-500/40 bg-red-500/5'
                  : tier === 'final' ? 'border-red-500/30 bg-red-500/[0.03]'
                  : tier === 'soon' ? 'border-yellow-500/30 bg-yellow-500/[0.03]'
                  : 'border-border/40'
                }`}
              >
                <div className="min-w-0 flex-1">
                  <Link to={`/programs/${l.program.id}`} className="text-sm font-medium hover:text-primary">
                    {l.program.title}
                  </Link>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
                    <span>{CATEGORY_LABELS[l.program.category]}</span>
                    {countdownLabel(l.program) && (
                      <span className={
                        expired ? 'text-red-400'
                        : tier === 'final' ? 'text-red-300'
                        : tier === 'soon' ? 'text-yellow-300' : ''
                      }>· {countdownLabel(l.program)}</span>
                    )}
                  </div>
                  {expired && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-red-400">
                      <AlertTriangle className="h-3 w-3" /> This program has expired
                    </div>
                  )}
                </div>
                <Button
                  size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={() => unlink(l.id, l.attached_by)} title="Unlink"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </CardContent>
      </Card>
      <AttachProgramDialog
        open={open} onOpenChange={setOpen}
        dealId={dealId} dealershipId={dealershipId}
        attachedIds={attachedIds} onAttached={refresh}
      />
    </>
  );
}